/* eslint-disable @typescript-eslint/ban-ts-comment */
/* eslint-disable react/no-unstable-nested-components */
import { Box, IconButton, Typography } from '@mui/material';
import { useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { RHFAutocomplete, Table } from 'src/components';
import { HandleSortChangeParams, TableColumn } from 'src/components/common/Table/types';
import RHFDateRangePicker from 'src/components/hook-form/RHFDateRangePicker';
import { Ul } from 'src/components/styled';
import { formatUsers } from 'src/components/TransactionSummary/utils';
import { getQuotesThunk, selectQuotes, setQuotesFilters, sortQuotes } from 'src/redux/slices/quoteSlice';
import { dispatch, useSelector } from 'src/redux/store';
import { Quote, QuoteTransaction, TransactionFlags } from 'src/redux/types/quoteSlice';
import { isOptionEqualToValue } from 'src/utils/common';
import { formatDate, formatPrice } from 'src/utils/transaction/summary';
import { capitalize } from 'lodash';
import Iconify from 'src/components/Iconify';
import { openModal } from 'src/redux/slices/modal';
import modals from 'src/constants/modals';
import { labelByQuoteStatus, quoteStageOptions, quoteStatusOptions } from './constants';
import UpdateQuoteModal from '../components/UpdateQuoteModal';

const QuotesTable = () => {
	const form = useForm();
	const quotes = useSelector(selectQuotes);

	useEffect(() => {
		const thunk = dispatch(getQuotesThunk({ withLoading: true }));

		return () => thunk.abort();
	}, [quotes.data.filters]);

	const data = form.watch();

	useEffect(() => {
		dispatch(setQuotesFilters(data));
	}, [data.creationDate, data.modificationDate]);

	const handleSortChange = (params: HandleSortChangeParams) => {
		dispatch(sortQuotes(params));
		dispatch(getQuotesThunk({ withLoading: true }));
	};

	const handleUpdateQuote = (quote: Quote) => () => {
		dispatch(openModal({ name: modals.updateQuote, payload: quote }));
	};

	const columns: TableColumn<Quote>[] = [
		{
			key: 'id',
			name: 'Quote ID',
			sortable: true,
			getLabel: (row) => (
				<Box display='flex' alignItems='center' gap='7px'>
					<IconButton onClick={handleUpdateQuote(row)}>
						<Iconify icon='material-symbols:edit-outline-rounded' fontSize={25} color='#A3A3A3' />
					</IconButton>
					{row.id}
				</Box>
			),
		},
		{
			key: 'clients',
			name: 'Clients',
			sortable: false,
			getLabel: (row) => <Box whiteSpace='nowrap'>{formatUsers(row.clients)}</Box>,
		},
		{
			key: 'parties',
			name: 'Intermediaries',
			sortable: false,
			getLabel: (row) => <Box whiteSpace='nowrap'>{formatUsers(row.intermediaries)}</Box>,
		},
		{
			key: 'referrers',
			name: 'Referrers',
			sortable: false,
			getLabel: (row) => <Box whiteSpace='nowrap'>{formatUsers(row.referrers ?? [])}</Box>,
		},
		{
			key: 'caseHandler',
			name: 'Owner',
			sortable: false,
			getLabel: (row) => (
				<Box whiteSpace='nowrap'>
					{row.caseHandler ? (
						<b>
							{row.caseHandler.givenNames} {row.caseHandler.lastNames}
						</b>
					) : (
						''
					)}
				</Box>
			),
		},
		{
			key: 'status',
			name: 'Status',
			sortable: true,
			getLabel: (row) => labelByQuoteStatus[row.status],
		},
		{
			key: 'modifiedAt',
			name: 'Modified At',
			sortable: true,
			getLabel: (row) => <Box whiteSpace='nowrap'>{formatDate(row.modifiedAt, 'DD MMM YYYY HH:MM z')}</Box>,
		},
		{
			key: 'createdAt',
			name: 'Created At',
			sortable: true,
			getLabel: (row) => <Box whiteSpace='nowrap'>{formatDate(row.createdAt, 'DD MMM YYYY HH:MM z')}</Box>,
		},
	];

	const subRowsColumns: TableColumn<QuoteTransaction>[] = [
		{ key: 'id', name: 'Transaction ID', width: 150, sortable: false },
		{ key: 'type', name: 'Type', width: 100, sortable: false },
		{ key: 'price', name: 'Price', width: 130, sortable: false, getLabel: (row) => formatPrice(row.price) },
		{
			key: 'adress',
			name: 'Address',
			width: 300,
			sortable: false,
			getLabel: (row) =>
				[row.address?.address, row.address?.townCity, row.address?.postcode, row.address?.county]
					.filter(Boolean)
					.join(', '),
		},
		{ key: 'tenure', name: 'Tenure', width: 150, sortable: false, getLabel: (row) => row.tenure },
		{
			key: 'flags',
			name: 'Flags',
			width: 200,
			sortable: false,
			getLabel: (row) => {
				const flags = Object.entries(row?.flags as TransactionFlags).filter(([, v]) => !!v);

				return (
					<Ul>
						{flags.map(([k]) => (
							<li key={k}>{capitalize(k.replaceAll('_', ' '))}</li>
						))}
					</Ul>
				);
			},
		},
		{
			key: 'total',
			name: 'Total',
			width: 150,
			sortable: false,
			getLabel: (row) => formatPrice(row.total?.net),
		},
	];

	const handleStatusChange = (_, options) => {
		form.setValue('status', options);

		dispatch(setQuotesFilters({ statuses: options.map((o) => o.id) }));
		dispatch(getQuotesThunk({ withLoading: true }));
	};

	const handleStageChange = (_, options) => {
		form.setValue('stage', options);

		dispatch(setQuotesFilters({ stages: options.map((o) => o.id) }));
		dispatch(getQuotesThunk({ withLoading: true }));
	};

	const handleParticipantsChange = (_, options) => {
		form.setValue('participants', options);

		dispatch(setQuotesFilters({ userEmails: options.map((o) => o.id) }));
		dispatch(getQuotesThunk({ withLoading: true }));
	};

	return (
		<Box display='flex' flexDirection='column' gap={2} height='100%'>
			<FormProvider {...form}>
				<Box sx={{ display: 'flex', gap: '20px', paddingTop: '10px' }}>
					<Box>
						<Typography variant='subtitle1' mb='3px'>
							Filter by Participants
						</Typography>

						<RHFAutocomplete
							// @ts-ignore
							multiple
							name='participants'
							placeholder={'Select Participants'}
							value={form.watch('participants') ?? []}
							sx={{ width: '450px' }}
							options={quotes.data.data.users.map((p) => ({ id: p.email, name: `${p.givenNames} ${p.lastNames}` }))}
							onChange={handleParticipantsChange}
							isOptionEqualToValue={isOptionEqualToValue('id')}
						/>
					</Box>
					<Box>
						<Typography variant='subtitle1' mb='3px'>
							Filter by Stage
						</Typography>
						<RHFAutocomplete
							// @ts-ignore
							multiple
							name='stage'
							label='Select Stage'
							value={form.watch('stage') ?? []}
							sx={{ width: '300px' }}
							options={quoteStageOptions}
							onChange={handleStageChange}
							isOptionEqualToValue={isOptionEqualToValue('id')}
						/>
					</Box>
					<Box>
						<Typography variant='subtitle1' mb='3px'>
							Filter by Status
						</Typography>
						<RHFAutocomplete
							// @ts-ignore
							multiple
							name='status'
							label='Select Status'
							value={form.watch('status') ?? []}
							sx={{ width: '300px' }}
							options={quoteStatusOptions}
							onChange={handleStatusChange}
							isOptionEqualToValue={isOptionEqualToValue('id')}
						/>
					</Box>
					<RHFDateRangePicker name='creationDate' label='Filter by Created at' />
					<RHFDateRangePicker name='modificationDate' label='Filter by Modified at' />
				</Box>
			</FormProvider>

			<Table
				data={{
					data: {
						data: quotes.data.data.quotes,
						limit: 0,
						page: 0,
						totalCount: 0,
						totalPages: 0,
						orderBy: quotes.data.orderBy,
						sortOrder: quotes.data.sortOrder,
					},
					isInitialized: quotes.isInitialized,
				}}
				columns={columns}
				subRowsColumns={subRowsColumns}
				getSubRowsData={(row) => row.transactions}
				handleSortChange={handleSortChange}
				rowKeyField='id'
				width={1750}
				subRowSx={{ background: '#f2f2f2' }}
			/>
			<UpdateQuoteModal />
		</Box>
	);
};

export default QuotesTable;
